import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Target, Zap, Award, Briefcase, Globe } from 'lucide-react';
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const AboutPage = () => {
  const location = useLocation();
  const pageTitle = "About WebFiny: Our Mission, Vision, and Team";
  const pageDescription = "Learn about WebFiny's journey, our commitment to revolutionizing document automation, and the values that drive our innovative PDF API solutions.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  const teamMembers = [
    { name: "Revathi Bose", role: "Founder & CEO", bio: "Visionary leader, passionate about simplifying complex document workflows and driving innovation in API technology.", isFounder: true, delay: 0.1 },
    { name: "Marcus Chen", role: "Chief Technology Officer", bio: "Expert architect of scalable cloud solutions, driving WebFiny's robust API infrastructure.", delay: 0.2 },
    { name: "Aisha Khan", role: "Head of Product", bio: "Dedicated to user-centric design, ensuring WebFiny APIs are intuitive and powerful.", delay: 0.3 },
    { name: "Raj Patel", role: "Lead Solutions Architect", bio: "Bridging customer needs with technical excellence, crafting bespoke automation solutions.", delay: 0.4 },
  ];

  const values = [
    { icon: <Zap className="w-10 h-10 text-primary" />, title: "Innovation", description: "Continuously pushing the boundaries of PDF technology." },
    { icon: <Users className="w-10 h-10 text-primary" />, title: "Customer-Centricity", description: "Our users are at the heart of everything we build." },
    { icon: <Award className="w-10 h-10 text-primary" />, title: "Excellence", description: "Striving for the highest quality in our products and services." },
    { icon: <Globe className="w-10 h-10 text-primary" />, title: "Integrity", description: "Operating with transparency and ethical practices." },
  ];

  const milestones = [
    { year: "Jan 2025", event: "Concept Born", description: "WebFiny conceptualized with a mission to simplify document processing for developers and businesses." },
    { year: "Mar 2025", event: "Core Team Assembled", description: "Founder Revathi Bose brings together a passionate team to build the initial platform." },
    { year: "Jun 2025", event: "Alpha API Development", description: "First set of core PDF APIs developed and tested internally." },
    { year: "Sep 2025", event: "Beta Program Launch", description: "WebFiny APIs released to a select group of early adopters for feedback." },
    { year: "Present", event: "Expanding & Refining", description: "Actively enhancing platform features based on beta user insights, preparing for public launch." },
  ];

  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": canonicalUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "publisher": {
              "@type": "Organization",
              "name": "WebFiny",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
              }
            }
          }
        ]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-4">
              About WebFiny
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              We're on a mission to empower developers and businesses with seamless, powerful, and intelligent document automation tools. Discover our story, values, and the team driving innovation at WebFiny.
            </p>
          </motion.div>

          <section className="mb-20 md:mb-28">
            <h2 className="text-3xl font-semibold text-center mb-6 text-foreground">Our Story & Mission</h2>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <img 
                  alt="WebFiny team collaborating on innovative solutions"
                  className="rounded-xl shadow-2xl object-cover w-full h-auto aspect-[3/2]"
                  width="600"
                  height="400"
                  src="https://images.unsplash.com/photo-1651009188116-bb5f80eaf6aa" />
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="space-y-4 text-muted-foreground"
              >
                <p>
                  Conceived in early 2025, WebFiny emerged from a simple yet powerful idea: document processing should be effortless and intelligent. We saw developers and businesses struggling with clunky, outdated PDF tools and knew there had to be a better way.
                </p>
                <p>
                  Our mission is to provide a cutting-edge API platform that not only handles complex PDF tasks with ease but also integrates seamlessly into modern development workflows. We believe in the power of automation to free up human potential for more creative and strategic endeavors.
                </p>
                <div className="flex items-center space-x-3 mt-4 p-4 bg-card rounded-lg shadow">
                  <Target className="w-12 h-12 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg text-foreground">Our Vision</h3>
                    <p className="text-sm">To be the leading global platform for intelligent document automation, transforming how businesses interact with information.</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          <section className="mb-20 md:mb-28">
            <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">Meet the Core Team</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {teamMembers.map((member) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: member.delay }}
                  className="bg-card p-6 rounded-xl shadow-lg text-center hover:shadow-primary/30 transition-shadow duration-300 flex flex-col"
                >
                  <div className="w-32 h-32 rounded-full mx-auto mb-4 bg-muted/50 flex items-center justify-center border-4 border-primary/50">
                    <Users className="w-16 h-16 text-primary/70" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-1">{member.name}</h3>
                  <p className="text-primary font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-muted-foreground flex-grow">{member.bio}</p>
                </motion.div>
              ))}
            </div>
          </section>
          
          <section className="mb-20 md:mb-28">
            <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">Our Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="bg-card p-8 rounded-xl shadow-lg text-center hover:shadow-accent/30 transition-shadow duration-300 flex flex-col items-center"
                >
                  <div className="p-4 bg-primary/10 rounded-full mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          <section className="mb-16 md:mb-24">
            <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">Our Journey So Far</h2>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-border -translate-x-1/2"></div>
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className={`mb-12 flex items-center ${index % 2 === 0 ? 'flex-row-reverse text-right' : 'text-left'}`}
                >
                  <div className="w-1/2 px-4">
                    <div className={`p-6 bg-card rounded-xl shadow-xl ${index % 2 === 0 ? 'ml-auto' : 'mr-auto'}`}>
                      <h3 className="text-xl font-bold text-primary mb-1">{milestone.year}</h3>
                      <h4 className="text-lg font-semibold text-foreground mb-2">{milestone.event}</h4>
                      <p className="text-sm text-muted-foreground">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-primary rounded-full absolute left-1/2 -translate-x-1/2 flex items-center justify-center text-background font-bold shadow-md">
                    {index + 1}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: milestones.length * 0.2 + 0.2 }}
            className="text-center"
          >
            <h2 className="text-3xl font-semibold text-foreground mb-6">Join Us on Our Journey</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              WebFiny is more than just a product; it's a community of innovators. Whether you're a developer looking for powerful APIs, a business seeking automation, or a talent wanting to make an impact, we'd love to connect.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <a
                href="/careers"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-background bg-primary hover:bg-primary/90 transition-colors shadow-lg hover:shadow-primary/40"
                aria-label="Explore career opportunities at WebFiny"
              >
                <Briefcase className="w-5 h-5 mr-2" /> Explore Careers
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-3 border border-border text-base font-medium rounded-md text-foreground bg-card hover:bg-muted transition-colors shadow-lg hover:shadow-muted/40"
                aria-label="Contact WebFiny for partnerships or inquiries"
              >
                <Users className="w-5 h-5 mr-2" /> Partner With Us
              </a>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  );
};

export default AboutPage;